// Admin Bridge Service
// Routes admin operations to admin-bridge edge function using simple JWT auth

import { isSupabaseConfigured } from "@/lib/supabase"
import { captureError } from '@/lib/sentry'
import type {
  BridgePayload,
  BridgeResult
} from '../contracts/apiBridge'

const ADMIN_AUTH_STORAGE_KEY = 'simple_admin_auth'

// Admin-specific actions supported by admin-bridge
export type AdminAction =
  | 'get_agent_submissions'
  | 'update_agent_submission_status'
  | 'create_agent_from_submission'
  | 'delete_agent'
  | 'list_enabler_submissions'
  | 'list_enablers'
  | 'update_enabler_submission_status'
  | 'approve_enabler'
  | 'list_agent_edits'
  | 'update_agent_edit_status'
  | 'list_enabler_edits'
  | 'update_enabler_edit_status'
  | 'admin_list_problem_statements'
  | 'admin_update_problem_status'

interface CallAdminBridgeOptions {
  envUrl?: string
  envAnonKey?: string
}

function getAdminToken(): string | null {
  try {
    const stored = sessionStorage.getItem(ADMIN_AUTH_STORAGE_KEY)
    if (!stored) return null
    const { token } = JSON.parse(stored)
    return token || null
  } catch {
    return null
  }
}

function getFunctionsBaseUrl(supabaseUrl: string): string {
  return supabaseUrl
    .replace('https://', 'https://')
    .replace('.supabase.co', '.functions.supabase.co')
}

export async function callAdminBridge<A extends AdminAction>(
  action: A,
  payload?: BridgePayload<A>,
  options: CallAdminBridgeOptions = {}
): Promise<BridgeResult<A>> {
  const { envUrl, envAnonKey } = options

  // Use environment-specific URL and key if provided, otherwise fall back to default
  const supabaseUrl = envUrl || import.meta.env.VITE_SUPABASE_URL
  const supabaseAnonKey = envAnonKey || import.meta.env.VITE_SUPABASE_ANON_KEY
  const functionsBaseUrl = getFunctionsBaseUrl(supabaseUrl)

  if (!functionsBaseUrl || !isSupabaseConfigured()) {
    throw new Error('Supabase functions URL not configured')
  }

  const adminToken = getAdminToken()
  if (!adminToken) {
    throw new Error('Admin authentication required')
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Admin-Token': adminToken
  }

  if (supabaseAnonKey) {
    headers['apikey'] = supabaseAnonKey
    headers['Authorization'] = `Bearer ${supabaseAnonKey}`
  }

  const response = await fetch(`${functionsBaseUrl}/admin-bridge`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, payload: payload || {} })
  })

  const rawText = await response.text()

  let data: any = {}
  if (rawText) {
    try {
      data = JSON.parse(rawText)
    } catch {
      data = { raw: rawText }
    }
  }

  if (!response.ok || data?.error) {
    const message = data?.error || rawText || response.statusText

    // Log to console for development
    console.error('Admin bridge request failed', {
      action,
      payload,
      status: response.status,
      response: data
    })

    // Capture to Sentry with admin context (scrub sensitive data)
    captureError(new Error(message), {
      tags: {
        action,
        admin_request: 'true',
        status_code: String(response.status),
      },
      extra: {
        // Don't include full payload for admin operations (may contain sensitive data)
        action_type: action,
        response_error: data?.error,
      },
    })

    throw new Error(message)
  }

  return data?.result as BridgeResult<A>
}
